<template>
  <div>
    <slot
      :pose-question="poseQuestion"
      :consent-box-shown="showConsentBox"
      :is-accepted="isAccepted"
      :redirect-to-alva="redirectToAlva"
    />

    <div
      v-if="showConsentBox && !isAccepted"
      class="rounded-[20px] flex flex-col h-full"
      :class="{
        'border border-primary-600 bg-primary-50 p-10 pb-20 lg:px-15':
          consentBoxStyle === 'green',
        'bg-gradient-to-b from-blue-900 to-blue-700 text-white p-25 lg:p-40 text-xl':
          consentBoxStyle === 'blue',
        'bg-alva-hello p-25 lg:p-40 text-xl': consentBoxStyle === 'hello',
      }"
    >
      <!-- Info text -->
      <div
        v-if="alvaLauncherData"
        class="ck-content mb-40 [&>p]:text-left flex-1"
        :class="{
          'is-inverted !text-lg': consentBoxStyle === 'blue',
          '!text-base':
            consentBoxStyle === 'unstyled' || consentBoxStyle === 'green',
          '[&>p>a]:!text-blue-900 [&>p>a]:visited:!text-blue-900 [&>p>a]:!decoration-blue-900 [&>p>a]:visited:!decoration-blue-900':
            consentBoxStyle === 'hello',
        }"
        v-html="alvaLauncherData.text"
      />

      <div
        class="flex justify-between flex-col sm:flex-row gap-20 items-start sm:items-center"
      >
        <!-- Alva button -->
        <AlvaButton :consent-box-style="consentBoxStyle" @click="acceptAlva" />

        <a
          v-if="alvaFaqLink"
          class="size-[42px] bg-white hover:bg-blue-200 rounded-full text-blue-900 flex items-center justify-center gap-10 text-base border transition-all ease-in-out duration-250 focus:outline-blue-900"
          :class="{
            'border-primary-100 focus:shadow-white': consentBoxStyle === 'blue',
          }"
          :href="alvaFaqLink"
          target="_blank"
        >
          <SpriteSymbol
            name="questionmark"
            class="size-20 rounded"
            aria-hidden="true"
          />
          <span class="sr-only">
            {{ $texts('alva.help', 'Mehr Informationen über Alva') }}
          </span>
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
const { isAccepted, accept } = useAlvaCookie()

const props = withDefaults(
  defineProps<{
    consentBoxStyle?: 'hello' | 'blue' | 'green' | 'unstyled'
    shouldRedirect?: boolean
  }>(),
  {
    consentBoxStyle: 'hello',
    shouldRedirect: true,
  },
)

const payload = ref('')
const showConsentBox = ref(false)

watch(
  () => isAccepted.value,
  (newValue: boolean) => {
    if (!newValue && !!payload.value) {
      showConsentBox.value = true
    }

    // In case both the questions and the manual input get interacted with. Can lead to weird results.
    if (newValue && payload.value) {
      showConsentBox.value = false
    }
  },
)

defineExpose({
  poseQuestion,
  isAccepted,
})

// Get Alva launcher data from config page.
// See /admin/config/translatable-config-pages/1/edit in backend.
const { data: alvaLauncherData } = await useAsyncGraphqlQuery(
  'alvaLauncherData',
  null,
  {
    transform: (v) => {
      return {
        url: v.data.globalConfig?.link?.uri?.path,
        text: v.data.globalConfig?.text,
      }
    },
    graphqlCaching: {
      client: true,
    },
  },
)

const { data: alvaFaqLink } = await useAsyncGraphqlQuery('alvaFaqLink', null, {
  transform: (v) => v.data.globalConfig?.faqLink?.uri?.path,
  graphqlCaching: {
    client: true,
  },
})

function redirectToAlva() {
  let url =
    alvaLauncherData.value?.url || 'https://baselcitygpt-api.gpt.liip.ch/'
  if (payload.value.length > 0) {
    url += '?q=' + encodeURIComponent(payload.value)
  }

  window.open(url, '_blank')?.focus()
}

function openAlva() {
  if (isAccepted.value) {
    if (props.shouldRedirect) {
      redirectToAlva()
    }
  } else {
    showConsentBox.value = true
  }
}

function poseQuestion(newPayload: string) {
  payload.value = newPayload

  openAlva()
}

function acceptAlva() {
  accept()
  showConsentBox.value = false
  openAlva()
}
</script>
